import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  FilteringState, SelectionState, IntegratedSelection, SortingState, PagingState, CustomPaging, TreeDataState,
  CustomTreeData, Filter, TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
  Grid, VirtualTable, Table, TableHeaderRow, TableFilterRow, TableSelection, PagingPanel, TableTreeColumn, TableColumnResizing,
  TableColumnReordering, DragDropProvider, TableColumnVisibility, ColumnChooser, Toolbar, PagingPanelProps,
} from '@devexpress/dx-react-grid-material-ui';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Checkbox, IconButton, Popover, TableCell } from '@mui/material';
import Paper from '@mui/material/Paper';
import debounce from 'lodash.debounce';
import TableLoading from '@/components/SearchTable/TableLoading/TableLoading';
import TableTooltip from '@/components/SearchTable/TableTooltip/TableTooltip';
import UsersTableHeader from '@/components/SearchTable/UsersTableHeader/UsersTableHeader';
import Button from '@/components/UI/Button/Button';
import DateRangerFilter from '@/components/UI/DateRangerFilter/DateRangerFilter';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import StatusFilterDropdown from '@/components/UI/StatusFilterDropdown/StatusFilterDropdown';
import {
  ADMIN, COMMITTEE_COORDINATOR, WORKFLOW_TYPE, errorTypes, propsForNonTypeScriptComponent, statusSubCampaign, HEADER_TO_FILTER,
  VERSION_DATE, CREATED, STATUS, ASC_ORDER, SUB_CR_PATH, CAPITAL_ASC, LAST_NAME, ERROR,
} from '@/helpers/constants';
import { buildOpensearchQueryData, opensearchFieldMapping, otherField } from '@/helpers/opensearch-utils';
import { columnsOrderMerge, mergeColumnExtensions, processSorting } from '@/helpers/tables-utils';
import { removeSpacesFromString, createRowLink, getCRidBySubCRid, isSubCRById } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import usersServices from '@/services/usersServices';
import {
  CampaignInfoOverview, FiltersOverview, GlobalFilters, SortOverview, SubCampaignInfoOverview, TableTranslations, TreeDataConfigOverview,
  UserCompleteInfo, VisibilityColumnsOverview,
} from '@/types';
import {
  columnSettingsToDefaultWidthColumns,
  defaultFilter, defaultFilterValues, defaultTableInfo, defaultDisabledColumnsForVisibility, getSelectionTableInfo,
  haveChildrensInThisPage, mapCampaingsRows, mapSubCampaignRows, tableMessages, columnSettingsToDefaultVisibleColumns,
  findFilterKey, getTimeToDebounce,
} from './search-table-utils';
import styles from './SearchTable.module.scss';

enum BUTTON_EXPANSION_STATE {
  COLLAPSED_ROWS = 'collapsedRows',
  EXPANDED = 'expanded',
  EXPANDED_SUBROWS = 'expandedSubRows',
}

export interface SearchTableRemoteProps {
  setSelectedOption: (selectedOption: (string)[]) => void;
  selectedOption?: (CampaignInfoOverview | SubCampaignInfoOverview)[];
  setSaveDisabled?: (saveDisabled: boolean) => void;
  setCampaignRequests?: (campaignRequests: CampaignInfoOverview[]) => void;
  setSubCampaignRequests?: (subCampaignsRequests: SubCampaignInfoOverview[]) => void;
  level?: number;
  isFixedLevel?: boolean;
  code?: string;
  isSupplier?: boolean;
  isCampaignOverview?: boolean;
  chipFilter?: string;
  timeZone?: string;
  resetData?: number;
  setFilters?: (filters: GlobalFilters) => void;
  filterColumnExtensions?: FilteringState.ColumnExtension[];
  sortColumnExtension?: SortingState.ColumnExtension[];
  columSorting?: SortOverview[];
  resetFilter?: number;
  filterOverview?: FiltersOverview;
  sortOverview?: SortOverview[];
  plant?: string;
  authorizedPlants?: boolean;
  campaignStatuses?: string[];
  isSubCROverview?: boolean;
  savedPageSize?: number;
  onRightClickHeaderRow?: (e: React.MouseEvent) => void;
  setExpandedRows?: (expandedRows: (string | number)[]) => void;
  expandedRows?: string[];
  comitteeCoordinatorBehavior?: boolean;
  treeDataConfig?: TreeDataConfigOverview;
  subCampaignRequests?: SubCampaignInfoOverview[];
  copyRow?: number;
  orderColumns?: string[];
  widthColumns?: TableColumnWidthInfo[];
  visibilityColumns?: VisibilityColumnsOverview[];
  userCompleteInfo?: UserCompleteInfo;
  defaultMergeProperty?: string;
  setOverviewFiltersFocus?: (field: any) => {
    type: string;
    payload: any;
  };
  tableTranslations?: TableTranslations;
  buttonTriggerLateralFilter?: string[];
}

const SearchTableRemote: React.FC<SearchTableRemoteProps> = (props) => {
  const { setSelectedOption, selectedOption = [], setSaveDisabled = () => null, setCampaignRequests = () => null, setSubCampaignRequests = () => null, level, isFixedLevel, code = '', isSupplier,
    isCampaignOverview, chipFilter, timeZone, resetData, setFilters = () => null, filterColumnExtensions, columSorting, resetFilter,
    filterOverview, sortOverview, plant, authorizedPlants, campaignStatuses, isSubCROverview, savedPageSize = 10,
    onRightClickHeaderRow = null, setExpandedRows = () => null, expandedRows = [], comitteeCoordinatorBehavior = false,
    treeDataConfig = null, copyRow = 0, orderColumns = [], widthColumns = [], visibilityColumns = [], sortColumnExtension = [], defaultMergeProperty = '',
    setOverviewFiltersFocus = () => null, tableTranslations, buttonTriggerLateralFilter = [] } = props;
  const authService = useAuth();
  const isSubCROrCROverview = isSubCROverview || isCampaignOverview;
  const [filterOverviewName, sortOverviewName, rowsPerPageName] = isSubCROverview
    ? ['filterSubCampaignOverview', 'sortSubCampaignOverview', 'rowsPerPageSubCampaignOverview']
    : ['filterOverview', 'sortOverview', 'rowsPerPageOverview'];
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [tableInfo, setTableInfo] = useState<any>(
    defaultTableInfo(!!isSupplier, !!isCampaignOverview, !!isSubCROverview, code, t, savedPageSize, { lang: lang, options: tableTranslations }),
  );
  const { data, rows, filters, selection, totalCount, pageSize, pageSizes, totalPages, currentPage, columns,
    columnExtensions, columnsNotTree, columnResizingExtensions } = tableInfo;
  const [filterValues, setFiltersValues] = useState(defaultFilterValues(!!isSupplier, code, !!isCampaignOverview));
  const [defaultSelection, setDefaultSelection] = useState(code);
  const [loading, setLoading] = useState(true);
  const createControlRef = useRef<HTMLDivElement>(null!);
  const messages = tableMessages(t, currentPage, totalPages, loading);
  const { addToast } = useToastContext();
  const [expandedRowsCounter, setExpandedRowsCounter] = useState<number>(0);
  const [buttonExpansionState, setButtonExpansionState] = useState<BUTTON_EXPANSION_STATE>(BUTTON_EXPANSION_STATE.COLLAPSED_ROWS);
  const [localSorting, setLocalSorting] = useState(sortOverview);
  const [localPageSize, setLocalPageSize] = useState(savedPageSize);
  const [isfirstLoad, setIsFirstLoad] = useState(true);
  const [localConfig, setLocalConfig] = useState({
    columnsWidth: widthColumns ? mergeColumnExtensions(widthColumns, columnSettingsToDefaultWidthColumns(columnExtensions)) : columnSettingsToDefaultWidthColumns(columnExtensions),
    columnsOrder: orderColumns ? columnsOrderMerge(orderColumns, columns.map((c) => c.name), defaultMergeProperty) : columns.map((c) => c.name), defaultMergeProperty,
    hiddenColumns: (visibilityColumns ? mergeColumnExtensions(visibilityColumns, columnSettingsToDefaultVisibleColumns(columnExtensions)) : columnSettingsToDefaultVisibleColumns(columnExtensions)).filter((c) => !c?.visible).map((c) => c.columnName),
  });

  // check filters of subCR and campaign
  const showColumnsSubCr = ((isCampaignOverview && expandedRows.length > 0) || !isCampaignOverview)
    || (filterValues?.some?.((filter) => [SUB_CR_PATH, 'campaign'].includes(filter.columnName) && filter.value !== '') ?? false);

  // when there are no campaigns with subCRs on the page, the arrows are not displayed
  const hasChildrensInThisPage = useMemo(
    () => haveChildrensInThisPage(rows, treeDataConfig?.childrenProperty) || showColumnsSubCr, [rows, treeDataConfig, showColumnsSubCr],
  );

  const currentVisibleColumnsWithAndOrder = useMemo(() => localConfig.columnsOrder
    .filter((column) => !localConfig.hiddenColumns.includes(column)), [localConfig]);
  const leftColumnNearToSelection = useMemo(() => (hasChildrensInThisPage ? (currentVisibleColumnsWithAndOrder[0] || treeDataConfig?.columnExpanded) : 'unmatchableText'),
    [currentVisibleColumnsWithAndOrder, hasChildrensInThisPage, treeDataConfig]);

  const onResetColumnsSettings = () => {
    setLocalConfig({
      columnsWidth: columnSettingsToDefaultWidthColumns(columnExtensions),
      columnsOrder: columns.map((c) => c.name),
      hiddenColumns: columnSettingsToDefaultVisibleColumns(columnExtensions).filter((c: any) => !c?.visible).map((c) => c.columnName),
    });
  };

  const getRowId = (row) => {
    if (isSupplier) return row.supplierId;
    if (isSubCROrCROverview) return row.id;

    return row.userCode;
  };

  const freeTextFilter = useMemo(() => filterOverview && filterOverview.find((filter) => filter.columnName === 'FREE_TEXT'),
    [filterOverview]);

  const activeFilters = useMemo(() => filterOverview && filterOverview.map((filter) => filter.columnName), [filterOverview]);

  const { goTo } = useGoTo();

  const handleLinkedRowOnClick = (row) => {
    goTo(createRowLink(row, isSubCROverview, freeTextFilter));
  };

  const anyExpanded = expandedRowsCounter > 0;

  const displayExtendedRows = (fromGetCampaigns = false) => {
    const expanded = rows.map((row) => row.id);
    let subCrsExpanded;

    if (buttonExpansionState === BUTTON_EXPANSION_STATE.EXPANDED_SUBROWS) {
      subCrsExpanded = rows.filter((row) => expanded.includes(row.id)).map((row) => row.childRows?.map((child) => child.id)).flat();
      expanded.push(...subCrsExpanded);
    }

    setExpandedRows(buttonExpansionState === BUTTON_EXPANSION_STATE.COLLAPSED_ROWS && !(authService.isSubCRRole || fromGetCampaigns) ? [] : expanded);
  };

  const changeButtonState = () => {
    setButtonExpansionState((prevState) => {
      if (prevState === BUTTON_EXPANSION_STATE.COLLAPSED_ROWS) return BUTTON_EXPANSION_STATE.EXPANDED;
      if (prevState === BUTTON_EXPANSION_STATE.EXPANDED) return BUTTON_EXPANSION_STATE.EXPANDED_SUBROWS;

      return BUTTON_EXPANSION_STATE.COLLAPSED_ROWS;
    });
  };

  useEffect(() => {
    displayExtendedRows();
  }, [buttonExpansionState]); // eslint-disable-line react-hooks/exhaustive-deps

  const Root = useCallback((rootProps) => (
    <Grid.Root
      {...rootProps}
      style={{
        height: isSubCROrCROverview ? `calc(100vh - ${authService.checkMultiRoleUser() ? '180' : '160'}px)` : '100%',
      }}
    />
  ), [authService.checkMultiRoleUser()]); // eslint-disable-line react-hooks/exhaustive-deps

  const Cell = useCallback((myProps) => {
    const { tableRow: { rowId, row }, column: { name: columnName }, tableColumn: { align } } = myProps;
    const contentClass = align === 'center' ? styles.CenterCell : '';

    if (isSubCROrCROverview) {
      return (
        <Table.Cell {...myProps} data-testid={`Table-Cell-${columnName}-${rowId}`}>
          <span className={styles.SpanTooltip}>
            <Link to={createRowLink(row, isSubCROverview, freeTextFilter)} className={contentClass}>
              {myProps.children}
            </Link>
          </span>
        </Table.Cell>
      );
    }

    return <Table.Cell {...myProps} data-testid={`Table-Cell-${columnName}-${rowId}`} className={contentClass} />;
  }, [freeTextFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const TableRow = ({ row, ...restProps }) => (
    isSubCROrCROverview
      ? (
        <Table.Row
          {...restProps}
          data-testid={`Table-Row-${row.id}`}
          className={`
            ${row.isSubCRChildren ? `${styles.SubCRExpandedButton} ${styles.RowOverviewSUBCR} ` : ' '}
            ${row?.thirdLevelRow ? styles.RowOverviewCampaignOfSUBCR : ' '}
            ${row?.fourthLevelRow ? styles.RowOverviewBlockingLocation : ' '}
          `}
          {...propsForNonTypeScriptComponent}
        />
      )
      : (
        <Table.Row
          {...restProps}
          data-testid={`Table-Row-${row.id}`}
          onClick={() => handleLinkedRowOnClick(row)}
          style={{ cursor: 'pointer' }}
          {...propsForNonTypeScriptComponent}
        />
      )
  );

  const FixedHeaderRow = ({ ...restProps }) => (
    <Table.Row
      {...restProps}
      {...propsForNonTypeScriptComponent}
      onContextMenu={onRightClickHeaderRow}
      style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 0,
      }}
    />
  );

  const CheckBoxRowComponent = useCallback(({ selected, onToggle, row, ...restProps }) => {
    const canBeApproved = (id) => {
      campaignServices.committeCoordinatorCanApprove(id)
        .then((res) => res === true)
        .catch((error) => addToast(error.message, ERROR));
    };

    const fatherSelected = row?.isSubCRChildren && comitteeCoordinatorBehavior && row?.id && selectedOption
      .find((cr) => cr.id === getCRidBySubCRid(row.id) && canBeApproved(cr.id));
    const isSubCRRow = row?.isSubCRChildren;
    // resolve console errors: React does not recognize the `tableColumn` or `tableRow` prop on a DOM element.
    delete restProps?.tableRow;
    delete restProps?.tableColumn;
    const subCRRowSelected = selectedOption.some((roww) => isSubCRById(roww?.id));
    /* const iAmBrotherOfSelected = selectedOption.some((roww) => getCRidBySubCRid(roww?.id) === getCRidBySubCRid(row.id)); */
    const cRRowSelected = selectedOption.some((roww) => !isSubCRById(roww?.id));

    return (
      <TableCell padding="checkbox" {...restProps}>
        {
          !row?.thirdLevelRow && !row?.fourthLevelRow && (
            <>
              {((!isSubCRRow || fatherSelected) && (!authService.isSubCRRole || authService.isAdmin))
                && (
                  <Checkbox
                    color="primary"
                    checked={selected || fatherSelected}
                    disabled={isSubCRRow || subCRRowSelected}
                    style={isSubCRRow ? { marginLeft: '30px' } : {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle();
                    }}
                  />
                )}
              {(isSubCRRow && (authService.isSubCRRole || authService.isAdmin))
                && (
                  <Checkbox
                    color="primary"
                    checked={selected}
                    disabled={cRRowSelected}
                    style={isSubCRRow ? { marginLeft: '30px' } : {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle();
                    }}
                  />
                )}
            </>
          )
        }
      </TableCell>
    );
  }, [comitteeCoordinatorBehavior, selectedOption, authService.isSubCRRole, authService.isAdmin, addToast]);

  const CheckBoxHeaderRowComponent = useCallback(({ disabled, allSelected, someSelected, onToggle, ...props }) => {
    delete props?.tableRow;
    delete props?.tableColumn;

    return (
      <TableCell padding="checkbox">
        <div className={styles.CheckBoxExpandedHeader}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            disabled={disabled}
            onClick={onToggle} // dont pass the event
            {...props}
          />
          <IconButton onClick={changeButtonState}>
            {anyExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          </IconButton>
        </div>
      </TableCell>
    );
  }, [loading, selection, expandedRowsCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  const ContentTreeColumn = (myProps) => {
    const { children: { props: { row } } } = myProps;

    const hasIcon = row?.isSubCRChildren && row?.statusCode === statusSubCampaign.IN_CONSOLIDATION
      && authService?.checkRoles([ADMIN, COMMITTEE_COORDINATOR]);

    return (
      <span className={styles.SpanTooltip}>
        <Link to={createRowLink(row, isSubCROverview, freeTextFilter)} className={hasIcon ? styles.LinkWithIcon : undefined}>
          {hasIcon
            ? <><i className={`di ${((row?.approvedByCampaignManager && row.workflowType === WORKFLOW_TYPE.DIGITAL) || row.workflowType !== WORKFLOW_TYPE.DIGITAL) ? 'icon-kreis-check Green' : 'icon-kreis-loeschen Red'} ${styles.Redirect}`} /> <div style={{ display: 'inline-block' }}>{myProps.children}</div> </>
            : myProps.children}
        </Link>
      </span>
    );
  };

  const updateSelectionWhenRowsChanges = () => (
    setTableInfo({ ...tableInfo, selection: defaultSelection ? [defaultSelection] : [] })
  );

  const findOptionSelected = (index: string[]) => data.find((row) => {
    if (isSupplier) return row.id === index;
    if (isSubCROrCROverview) return index.includes(row.id);

    return row.userCode === index;
  });

  const selectUser = (index) => {
    const userSelected = index
      ? { ...findOptionSelected(index), selected: true }
      : { lastName: '', firstName: '', department: '', selected: false };
    setDefaultSelection(userSelected.selected ? userSelected.userCode : '');
    setSelectedOption(userSelected);
  };

  const selectSupplier = (index) => {
    const supplierSelected = index ? findOptionSelected(index) : null;
    setDefaultSelection(supplierSelected?.id || '');
    setSelectedOption(supplierSelected);
  };

  const selectCampaign = (index: string[]) => {
    const campaignSelected: CampaignInfoOverview | SubCampaignInfoOverview | null = findOptionSelected(index);
    setDefaultSelection(campaignSelected?.id || '');
    setSelectedOption(index);
  };

  const selectOption = (rowClicked: (string | number) | (string | number)[]) => {
    const selectionn = isSubCROrCROverview ? rowClicked : rowClicked[0];

    if (isSupplier) {
      selectSupplier(selectionn);
      setSaveDisabled(false);
    } else if (isSubCROrCROverview) {
      selectCampaign(selectionn);
    } else {
      selectUser(selectionn);
      setSaveDisabled(false);
    }
  };

  const handleSelectRow = (newSelection: (string | number)[]) => {
    const rowClicked = newSelection.filter((index) => !(selection).includes(index));
    let finalSelection = isSubCROrCROverview ? newSelection : rowClicked;

    if (isSubCROrCROverview && rowClicked[0]) {
      if (isSubCRById(rowClicked[0]) && selection.some((index) => !isSubCRById(index))) { // if subCR is selected and CR is not in selection
        finalSelection = rowClicked;
      } else if (selection.some((index) => isSubCRById(index)) && !isSubCRById(rowClicked[0])) { // if CR is selected and subCR is in selection
        finalSelection = rowClicked;
      }
    } else if (isSubCROrCROverview && newSelection.length === data.length) { // check all rows are selected and unselect all
      setTableInfo({ ...tableInfo, selection: [] });
      selectOption([]);

      return;
    }

    setTableInfo({ ...tableInfo, selection: finalSelection });
    selectOption(finalSelection);
  };

  const mapSupplierRows = (result = []) => result.map((row) => {
    const { id, addressCity, addressCountry, addressPostalCode, addressStreet, name } = row;

    const supplierName = [name, addressCountry, addressPostalCode, addressCity, addressStreet]
      .filter((stringText) => removeSpacesFromString(stringText))
      .join(', ');

    return { supplierId: id, supplierName };
  });

  useEffect(() => {
    const newSelection = tableInfo.selection.filter((selectionItem) => tableInfo.data.some((item: any) => item?.id === selectionItem));
    setTableInfo((prevState) => ({ ...prevState, selection: newSelection }));
    displayExtendedRows(true);
  }, [tableInfo.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRows = (responseSuppliers, firstRender) => {
    if (isSupplier) {
      const index = responseSuppliers.findIndex(({ id }) => id === code);

      return mapSupplierRows(firstRender && code && index !== -1 ? [responseSuppliers[index]] : responseSuppliers);
    }

    if (isCampaignOverview) return mapCampaingsRows(responseSuppliers, lang, timeZone, t, <i className="di icon-pfeil-chevron-rechts" />);
    if (isSubCROverview) return mapSubCampaignRows(responseSuppliers, lang, timeZone, t, <i className="di icon-pfeil-chevron-rechts" />);

    return responseSuppliers;
  };

  const calculatePages = (res, firstRender) => {
    if (isSupplier) return isSupplier && firstRender && code ? 1 : res.totalPages * res.pageSize;
    if (isSubCROrCROverview) return res.totalElements;

    return res.totalPages * res.pageSize;
  };

  const setTableParams = (res, filter, firstRender) => {
    if (!res) return;

    setTableInfo((prevState) => {
      let newData;
      let newRows;

      if (isSubCROrCROverview) {
        newData = res.content;
        newRows = getRows(res.content, firstRender);
        setExpandedRows([]);
      } else {
        newData = res.results;
        newRows = getRows(res.results, firstRender);
      }

      const updatedInfo = {
        ...prevState,
        columns: defaultTableInfo(!!isSupplier, !!isCampaignOverview, !!isSubCROverview, code, t, undefined, { lang, options: tableTranslations }).columns,
        filters: filter,
        data: newData,
        rows: newRows,
        totalPages: res.totalPages,
        currentPage: isSubCROrCROverview ? res.number : res.pageNumber,
        pageSize: res.pageSize || res.size || tableInfo.pageSize || 50,
        totalCount: calculatePages(res, firstRender),
      };

      if (!isSubCROrCROverview) {
        updatedInfo.selection = getSelectionTableInfo(firstRender, defaultSelection, prevState.selection);
      } else if (!selectedOption.length) updatedInfo.selection = [];

      return updatedInfo;
    });
  };

  const fetchUsersLevelFilterTable = useApiRequest(usersServices.getUsersLevelFilterTable);
  const fetchUsersFilterTable = useApiRequest(usersServices.getUsersFilterTable);

  const getUsers = (filter, page, rowsPerPage, firstRender) => {
    setLoading(true);

    if (plant) filter.plant = plant;
    if (authorizedPlants) filter.authorizedPlants = true;

    if (level) {
      fetchUsersLevelFilterTable(level, filter, page, rowsPerPage, LAST_NAME, CAPITAL_ASC, isFixedLevel)
        .then((res) => setTableParams(res, filter, firstRender))
        .catch((error) => {
          if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, ERROR);
        }).finally(() => setLoading(false));
    } else {
      fetchUsersFilterTable(filter, page, rowsPerPage)
        .then((res) => setTableParams(res, filter, firstRender))
        .catch((error) => {
          if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, ERROR);
        }).finally(() => setLoading(false));
    }
  };

  const sortingOptions = columSorting || [{ columnName: columns[0]?.name, direction: ASC_ORDER }];
  const [sorting, setSorting] = useState(sortingOptions);

  const fetchCampaigns = useApiRequest(campaignServices.getCampaignRequestsWithOpenSearch);

  const calculateVisibleRows = () => {
    const expandedCRs = rows.filter((campaign) => expandedRows.includes(campaign.id));
    const expandedSubCRs = expandedCRs.flatMap((expandedCR) => expandedCR.childRows || []).filter((subCR) => expandedRows.includes(subCR.id));
    const expandedCampaigns = expandedSubCRs.flatMap((subCR) => subCR.childRows || []).filter((campaign) => expandedRows.includes(campaign.id));
    const totalRows = expandedCRs.reduce((acc, current) => acc + (current?.childRows?.length ?? 0), 0)
      + expandedSubCRs.reduce((acc, current) => acc + (current?.childRows?.length ?? 0), 0)
      + expandedCampaigns.reduce((acc, current) => acc + (current?.childRows?.length ?? 0), 0);

    return totalRows;
  };

  useEffect(() => {
    setExpandedRowsCounter(calculateVisibleRows());
  }, [expandedRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSetExpandedRows = (exRows) => {
    if (exRows.length > expandedRows.length) {
      const newCRExpanded = exRows.filter((row) => !expandedRows.includes(row));
      const subCRs = rows.filter((row) => newCRExpanded.includes(row?.mainCR)).map((row) => row?.childRows).flat()
        .filter((subCR) => subCR?.childRows?.length > 0)
        .map((row) => row?.id);

      return setExpandedRows(exRows.concat(subCRs));
    }
    const removedRow = expandedRows.find((row) => !exRows.includes(row));
    const expandedRowsFiltered = exRows.filter((row) => row !== removedRow && !row.toString().includes(removedRow?.toString()));

    setExpandedRows(expandedRowsFiltered);
  };

  const getCampaigns = (filter, page, size, firstRender, sort = opensearchFieldMapping.created, sortDir = CAPITAL_ASC, childSort, childSortDir, filterChip) => {
    const filterKey = findFilterKey(sort);
    const needTranslation = filterKey === 'campaigns.title.translations';
    fetchCampaigns(
      filterChip,
      buildOpensearchQueryData(filter, isSubCROverview),
      page,
      size,
      processSorting((childSort ? opensearchFieldMapping.created : filterKey) as string),
      childSort ? CAPITAL_ASC : sortDir,
      processSorting(childSort),
      childSortDir,
      needTranslation && lang,
    ).then((res) => {
      if (res) {
        setTableParams(res, filter, firstRender);
        setCampaignRequests(res?.content);

        if (authService.isSubCRRole) {
          setExpandedRows(res.content.map((campaign) => campaign.id));
        }
        setSubCampaignRequests(res.content.map((campaign) => campaign.subCampaigns).flat());
        setIsFirstLoad(false);
      }
    })
      .catch((error) => {
        if (error?.name !== errorTypes.ABORT_ERROR && error?.message) {
          addToast(error.message, ERROR);
        }
        throw error;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchSuppliers = useApiRequest(usersServices.getSuppliers);

  const getSuppliers = (filter, page, size, firstRender) => {
    setLoading(true);
    fetchSuppliers(filter, page, size)
      .then((res) => setTableParams(res, filter, firstRender))
      .catch((error) => {
        if (error?.message) addToast(error.message, ERROR);
      })
      .finally(() => setLoading(false));
  };

  const getTableData = (filter, page, rowsPerPage, firstRender, sort = sorting, filterChip) => {
    if (isSupplier) return getSuppliers(filter, page, rowsPerPage, firstRender);

    if (isSubCROrCROverview) {
      const isSubCR = sort[0]?.columnName === otherField[1].key;

      return getCampaigns(
        filter,
        page,
        rowsPerPage,
        firstRender,
        sort[0]?.columnName,
        sort[0]?.direction?.toUpperCase(),
        isSubCR ? otherField[1].value : '',
        isSubCR ? sort[0]?.direction?.toUpperCase() : '',
        filterChip,
      );
    }

    return getUsers(filter, page, rowsPerPage, firstRender);
  };

  const debouncedGetData = useCallback(
    debounce(
      (filter, page, rowsPerPage, firstRender, sort, filterChip) => getTableData(filter, page, rowsPerPage, firstRender, sort, filterChip),
      getTimeToDebounce(!!isSubCROrCROverview, isfirstLoad),
    ), [timeZone, lang, isfirstLoad], // patch until ticket 9398
  );

  const debouncedSetFilters = useMemo(
    () => debounce(
      (filterConfig) => {
        setLoading(true);
        setFilters(filterConfig);
      },
      getTimeToDebounce(!!isSubCROrCROverview, isfirstLoad, true),
    ), [setFilters, isSubCROrCROverview, isfirstLoad],
  );

  const handlePageChange = (newPage: number) => {
    setLoading(true);

    if (!tableInfo.lastPage) {
      getTableData(filters, newPage, pageSize, false, sorting, chipFilter);
    }
  };

  const mapColumn = (column) => {
    if (!isSupplier && column === 'userCode') return 'userId';

    return column;
  };

  const handlePageSizeChange = (newPageSize) => {
    if (isSubCROrCROverview) setFilters({ [rowsPerPageName]: newPageSize });
    setLoading(true);
    getTableData(filters, 0, newPageSize, false, sorting, chipFilter);
    setTableInfo({ ...tableInfo, pageSize: newPageSize });
  };

  const handleSorting = (sort) => {
    if (isSubCROrCROverview) {
      setFilters({ [sortOverviewName]: JSON.stringify(sort) });
    }
    setSorting(sort);
  };

  const handleCampaignFilterChange = (newFilter: Filter[]) => {
    const filterOverviewTransformed = filterOverview?.map((f) => ({ columnName: f.columnName, value: f.value })) || [];

    const valueFilter = filterOverviewTransformed.filter((element) => element?.columnName?.match(/^[A-Z]/));

    const initialValue = valueFilter.length > 0
      ? valueFilter.reduce((acc, { columnName, value }) => ({ ...acc, [mapColumn(columnName)]: value }), {})
      : defaultFilter(!!isSupplier, code, isCampaignOverview, false);
    const filter = newFilter
      .reduce((acc, { columnName, value }) => ({ ...acc, [mapColumn(columnName)]: value }), initialValue);

    const configFilter = Object.entries(filter)
      .map((value) => ({ columnName: mapColumn(value[0]), value: value[1] }));

    setFiltersValues(newFilter as any);
    debouncedSetFilters({ [filterOverviewName]: JSON.stringify(configFilter) });
  };

  const handleFilterChange = (newFilter: Filter[]) => {
    const initialValue = defaultFilter(!!isSupplier);
    const filter = newFilter
      .reduce((acc, { columnName, value }) => ({ ...acc, [mapColumn(columnName)]: value }), initialValue);
    setFiltersValues(newFilter as any);
    debouncedGetData(filter, 0, pageSize);
  };

  const FilterCell = useCallback((cellProps) => {
    if (cellProps?.column?.name === '') return null;
    const isActive = cellProps.children[1].props.value?.length > 0 || false;

    if (!buttonTriggerLateralFilter.includes(cellProps.column.name) && ![VERSION_DATE, CREATED, STATUS].includes(cellProps.column.name))
      return (
        <TableFilterRow.Cell
          id={`filter-${cellProps?.column?.name}`}
          key={`filter-${cellProps?.column?.name}`}
          className={`${styles.FilterCell} ${isActive ? styles.ActiveFilterCell : ''}`}
          {...cellProps}
          style={{
            ...cellProps?.style,
            paddingLeft: cellProps.column.name !== leftColumnNearToSelection ? 0 : 50,
          }}
        />
      );

    return (
      <TableFilterRow.Cell
        {...cellProps}
        id={`filter-${cellProps?.column?.name}`}
        key={`filter-${cellProps?.column?.name}`}
        style={{
          ...cellProps?.style,
          padding: 0,
          paddingLeft: cellProps.column.name !== leftColumnNearToSelection ? 0 : 50,
        }}
      >
        {(cellProps.column.name === STATUS)
          ? (
            <StatusFilterDropdown
              id={`filter-${cellProps?.column?.name}`}
              statuses={campaignStatuses}
              isSubCr={isSubCROverview}
              classes={`${styles.FilterCell} ${isActive ? styles.ActiveFilterCell : ''}`}
              {...cellProps}
            />
          )
          : [VERSION_DATE, CREATED].includes(cellProps.column.name)
            ? (
              <DateRangerFilter
                id={`filter-${cellProps?.column?.name}`}
                classes={`${styles.FilterCell} ${isActive ? styles.ActiveFilterCell : ''}`}
                {...cellProps}
              />
            )
            : (buttonTriggerLateralFilter.includes(cellProps.column.name))
              ? (
                <Button
                  handleClick={() => setOverviewFiltersFocus(HEADER_TO_FILTER[cellProps.column.name])}
                  btnText={t('openFilter')}
                  extraButtonClasses={activeFilters?.includes(HEADER_TO_FILTER[cellProps.column.name]) ? `${styles.BtnFilterActive} ${styles.BtnFilter}` : `${styles.BtnFilter}`}
                />
              )
              : null}
      </TableFilterRow.Cell>
    );
  }, [campaignStatuses, leftColumnNearToSelection, activeFilters?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUserConfigToSave = () => {
    const columnsVisibility = columns.map((column) => ({
      columnName: column.name,
      visible: !localConfig.hiddenColumns.includes(column.name),
    }));

    return ({
      orderColumnsOverview: JSON.stringify(localConfig.columnsOrder),
      widthColumnsOverview: JSON.stringify(localConfig.columnsWidth),
      visibilityColumnsOverview: JSON.stringify(columnsVisibility),
    });
  };

  useEffect(() => {
    let filterTable: any = filters;
    const sortTable = sortOverview || sortingOptions;

    if (isSubCROrCROverview) {
      filterTable = filterOverview?.map((f) => ({ columnName: f.columnName, value: f.value })) || [];
      const newFilter = filterTable.filter((element) => !element.columnName.match(/^[A-Z]/));
      filterTable = filterTable.reduce((acc, { columnName, value }) => ({ ...acc, [mapColumn(columnName)]: value }), {});
      setSorting(sortTable);
      setFiltersValues(newFilter);

      if (JSON.stringify(filterTable) !== JSON.stringify(filters) || JSON.stringify(sortTable) !== JSON.stringify(localSorting)
        || pageSize !== localPageSize || loading) {
        setLoading(true);
        setLocalSorting(sortTable);
        setLocalPageSize(pageSize);
        debouncedGetData(filterTable, 0, pageSize, false, sortTable, chipFilter);
      }
    } else {
      setLoading(true);
      debouncedGetData(filterTable, 0, pageSize, false, sortTable, chipFilter);
    }
  }, [filterOverview, sortOverview, savedPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chipFilter) {
      setLoading(true);
      debouncedGetData(filters, 0, pageSize, false, sorting, chipFilter);
    }
  }, [chipFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resetFilter && resetFilter > 0) {
      setFilters({ [filterOverviewName]: JSON.stringify(filterOverview?.map((f) => ({ columnName: f.columnName, value: '' })) || []), [sortOverviewName]: null });
      setSorting(columSorting || []);
      handleSelectRow([]);
      setSelectedOption([]);
      setFiltersValues(defaultFilterValues(!!isSupplier, code, !!isCampaignOverview, isSubCROverview));
    }
  }, [resetFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resetData) {
      setLoading(true);
      debouncedGetData(filters, currentPage, pageSize, false, sorting, chipFilter);
    }
    updateSelectionWhenRowsChanges();
  }, [resetData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTableInfo({
      ...tableInfo,
      columns: defaultTableInfo(!!isSupplier, !!isCampaignOverview, !!isSubCROverview, code, t, undefined, { lang: lang, options: tableTranslations }).columns,
      columnsNotTree: defaultTableInfo(!!isSupplier, !!isCampaignOverview, !!isSubCROverview, code, t, undefined, { lang: lang, options: tableTranslations }).columnsNotTree,
      rows: getRows(data, false),
    });
  }, [lang, timeZone, tableTranslations]); // eslint-disable-line react-hooks/exhaustive-deps

  const onColumnWidthsChange = (columnsWidth: TableColumnWidthInfo[]) => setLocalConfig((prevConfig) => ({ ...prevConfig, columnsWidth }));
  const onColumnsOrderChange = (columnsOrder: string[]) => setLocalConfig((prevConfig) => ({ ...prevConfig, columnsOrder }));
  const onColumnsVisibilityChange = (hiddenColumns: string[]) => setLocalConfig((prevConfig) => ({ ...prevConfig, hiddenColumns }));

  useEffect(() => {
    if (isCampaignOverview) {
      const configToSave = getUserConfigToSave();
      setFilters(configToSave as unknown as GlobalFilters);
    }
  }, [localConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabledColumnsForVisibility = useMemo(() => columns.reduce((acc, column) => (
    (filterValues?.some?.((filter) => (column?.name === filter?.columnName && filter?.value !== '')))
      ? [...acc, { columnName: column.name, togglingEnabled: false }]
      : acc
  ), defaultDisabledColumnsForVisibility), [columns, filterValues]);

  const VisibilityColumnButton = useCallback((propss: ColumnChooser.ToggleButtonProps) => (
    <div ref={createControlRef} className={styles.VisibilityColumnButton}>
      <IcoButtonSimple
        id="btn-visibility-columns"
        iconComponent={<i className="di icon-layout" />}
        tooltipText={t('campaignRequests.overviewSettings')}
        handleClick={propss.onToggle}
        {...propss}
      />
    </div>
  ), [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const VisibilityColumnOverlay = useCallback((propss: ColumnChooser.OverlayProps) => (
    <Popover
      open={propss.visible}
      anchorEl={createControlRef.current}
      onClose={propss.onHide}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      {...propss}
    >
      <div className={styles.ColumnVisibilityHeader}>{t('campaignRequests.overviewSettings')}</div>
      {propss.children}
      <div className={styles.ResetConfigContainer}>
        <SimpleButton
          id="btn-reset-columns-settings"
          color="primary"
          buttonText={t('campaignRequests.resetColumnSettings')}
          handleClick={onResetColumnsSettings}
          buttonClassName="w-100"
        />
      </div>
    </Popover>
  ), [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!isSubCROrCROverview && (<UsersTableHeader filters={filterValues} handleFilterChange={handleFilterChange} />)}
      <Paper style={{ position: 'relative' }} className={isSubCROrCROverview ? styles.LinkTable : ''}>
        <Grid
          key={copyRow}
          rows={rows}
          columns={showColumnsSubCr ? columns : columnsNotTree}
          getRowId={getRowId}
          rootComponent={Root}
        >
          {isSubCROrCROverview
            ? (
              <FilteringState
                columnExtensions={filterColumnExtensions}
                filters={filterValues}
                onFiltersChange={handleCampaignFilterChange}
              />
            )
            : (
              <FilteringState
                filters={filterValues}
                onFiltersChange={handleFilterChange}
              />
            )}
          <SelectionState
            selection={selection}
            onSelectionChange={handleSelectRow}
          />
          {isSubCROrCROverview && (
            <SortingState
              sorting={sorting}
              onSortingChange={handleSorting}
              columnExtensions={sortColumnExtension}
            />
          )}
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          <CustomPaging
            totalCount={totalCount}
          />
          <TableTooltip
            columns={columns}
            columnExtensions={columnExtensions}
          />
          {isSubCROrCROverview && <DragDropProvider />}

          <IntegratedSelection />
          {treeDataConfig && (<TreeDataState expandedRowIds={expandedRows} onExpandedRowIdsChange={onSetExpandedRows} />)}
          {treeDataConfig && (<CustomTreeData getChildRows={(row, rootRows) => (row ? row?.[treeDataConfig.childrenProperty] : rootRows)} />)}

          {isSubCROrCROverview
            ? (
              <VirtualTable
                columnExtensions={columnExtensions}
                messages={messages.table}
                rowComponent={TableRow}
                cellComponent={Cell}
              />
            )
            : (
              <VirtualTable
                columnExtensions={columnExtensions}
                messages={messages.table}
              />
            )}

          {isSubCROrCROverview && (
            <TableColumnResizing
              onColumnWidthsChange={onColumnWidthsChange}
              columnWidths={localConfig?.columnsWidth}
              columnExtensions={columnResizingExtensions}
            />
          )}

          {isSubCROrCROverview && (
            <TableColumnReordering
              order={localConfig?.columnsOrder}
              onOrderChange={onColumnsOrderChange}
            />
          )}

          <TableHeaderRow showSortingControls={isSubCROrCROverview} rowComponent={FixedHeaderRow} />

          {isSubCROrCROverview && (
            <TableColumnVisibility
              hiddenColumnNames={localConfig?.hiddenColumns}
              columnExtensions={disabledColumnsForVisibility}
              onHiddenColumnNamesChange={onColumnsVisibilityChange}
            />
          )}
          {isSubCROrCROverview && <Toolbar />}
          {isSubCROrCROverview && (
            <ColumnChooser
              toggleButtonComponent={VisibilityColumnButton}
              overlayComponent={VisibilityColumnOverlay}
            />
          )}

          <TableFilterRow
            cellComponent={FilterCell}
            messages={messages.filter}
          />
          {treeDataConfig && (
            <TableTreeColumn for={leftColumnNearToSelection} contentComponent={ContentTreeColumn} />
          )}
          {!isSubCROrCROverview && (<TableSelection selectByRowClick />)}
          {(isSubCROrCROverview && hasChildrensInThisPage)
            && (
              <TableSelection
                showSelectAll={isSubCROrCROverview}
                cellComponent={CheckBoxRowComponent}
                headerCellComponent={CheckBoxHeaderRowComponent}
              />
            )}

          {(isSubCROrCROverview && !hasChildrensInThisPage)
            && (<TableSelection showSelectAll={isSubCROrCROverview} cellComponent={CheckBoxRowComponent} />)}

          <PagingPanel pageSizes={pageSizes} messages={messages.paging as PagingPanelProps['messages']} />

          {loading && <TableLoading styles={styles} />}
        </Grid>
      </Paper>
    </>
  );
};

export default SearchTableRemote;
